/**
 * Procedural pixel textures generated via Canvas 2D at runtime.
 *
 * Why procedural?  Issue #4 mandates "keine komplexen Assets erforderlich"
 * (Issue #2) plus low-res pixel art with sharp filtering.  Hand-painted PNGs
 * would also work; procedural keeps the repo asset-free and makes the look
 * tweakable from code.  Every texture uses NEAREST sampling so pixels stay
 * crisp (Issue #4 acceptance criterion).
 */

import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Scene } from '@babylonjs/core/scene';

type Draw = (ctx: CanvasRenderingContext2D) => void;

function px(size: number, draw: Draw, name: string, scene: Scene): Texture {
  const tex = new DynamicTexture(name, { width: size, height: size }, scene, false);
  const ctx = tex.getContext() as unknown as CanvasRenderingContext2D;
  draw(ctx);
  tex.update(false);
  tex.updateSamplingMode(Texture.NEAREST_SAMPLINGMODE);
  tex.wrapU = Texture.WRAP_ADDRESSMODE;
  tex.wrapV = Texture.WRAP_ADDRESSMODE;
  return tex;
}

// Simple deterministic PRNG so the textures look the same on every reload
// (otherwise the speckle pattern would shift between sessions).
function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** 64×64 grass with subtle pixel speckle. */
export function grassTexture(scene: Scene): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#3e5a2a';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(1);
    for (let i = 0; i < 600; i++) {
      const c = rnd();
      ctx.fillStyle = c < 0.5 ? '#4a6a32' : '#2e4a20';
      ctx.fillRect(Math.floor(rnd() * 64), Math.floor(rnd() * 64), 1, 1);
    }
  }, 'tex.grass', scene);
}

/** 64×64 stone floor — big cobbles with grout. */
export function stoneFloorTexture(scene: Scene): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#4a4036';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(2);
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 4; col++) {
        const x = col * 16 + (row % 2 === 0 ? 0 : 8);
        const y = row * 16;
        ctx.fillStyle = rnd() < 0.5 ? '#736a5a' : '#857a68';
        ctx.fillRect(x, y, 16, 16);
        ctx.fillStyle = '#2a241c';
        ctx.fillRect(x, y + 15, 16, 1);
        ctx.fillRect(x + 15, y, 1, 16);
      }
    }
    for (let i = 0; i < 120; i++) {
      ctx.fillStyle = `rgba(0,0,0,${rnd() * 0.25})`;
      ctx.fillRect(Math.floor(rnd() * 64), Math.floor(rnd() * 64), 1, 1);
    }
  }, 'tex.stoneFloor', scene);
}

/** 64×64 brick wall — offset rows for that medieval-crypt feel. */
export function stoneWallTexture(scene: Scene): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#3a3328';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(3);
    for (let row = 0; row < 8; row++) {
      const y = row * 8;
      const offset = row % 2 === 0 ? 0 : 8;
      for (let col = -1; col < 9; col++) {
        const x = col * 16 + offset;
        ctx.fillStyle = rnd() < 0.5 ? '#7a6c58' : '#8a7c66';
        ctx.fillRect(x + 1, y + 1, 14, 6);
        ctx.fillStyle = '#1c1812';
        ctx.fillRect(x, y, 16, 1);
        ctx.fillRect(x, y + 7, 16, 1);
        ctx.fillRect(x, y, 1, 8);
        ctx.fillRect(x + 15, y, 1, 8);
      }
    }
    for (let i = 0; i < 200; i++) {
      ctx.fillStyle = `rgba(0,0,0,${rnd() * 0.2})`;
      ctx.fillRect(Math.floor(rnd() * 64), Math.floor(rnd() * 64), 1, 1);
    }
  }, 'tex.stoneWall', scene);
}

/** 64×64 wooden plank texture (for doors, beams). */
export function woodPlankTexture(scene: Scene): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#3d2a18';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(4);
    for (let i = 0; i < 5; i++) {
      const x = i * 13;
      ctx.fillStyle = i % 2 === 0 ? '#5a3e22' : '#4a3320';
      ctx.fillRect(x, 0, 12, 64);
      ctx.fillStyle = '#1a0f06';
      ctx.fillRect(x + 12, 0, 1, 64);
      // grain
      for (let g = 0; g < 6; g++) {
        ctx.fillStyle = 'rgba(0,0,0,0.25)';
        ctx.fillRect(x + Math.floor(rnd() * 12), Math.floor(rnd() * 64), 1, 1);
      }
    }
  }, 'tex.woodPlank', scene);
}

/** 64×64 red-clay roof tile pattern. */
export function roofTileTexture(scene: Scene): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#3a1a14';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(5);
    for (let row = 0; row < 8; row++) {
      const y = row * 8;
      const offset = row % 2 === 0 ? 0 : 8;
      for (let col = -1; col < 9; col++) {
        const x = col * 16 + offset;
        ctx.fillStyle = rnd() < 0.5 ? '#7a2820' : '#5a2018';
        ctx.fillRect(x + 1, y, 14, 7);
        ctx.fillStyle = '#2a0a06';
        ctx.fillRect(x, y + 7, 16, 1);
      }
    }
  }, 'tex.roof', scene);
}

/** 32×32 pixel sprite for an NPC — a hooded figure. */
export function npcSpriteTexture(scene: Scene): Texture {
  return px(32, (ctx) => {
    ctx.clearRect(0, 0, 32, 32);
    // hooded figure: dark robe + face
    // body
    ctx.fillStyle = '#2a2a3a';
    ctx.fillRect(10, 16, 12, 14);
    // shoulders
    ctx.fillRect(8, 14, 16, 4);
    // hood
    ctx.fillStyle = '#1a1a24';
    ctx.fillRect(10, 4, 12, 12);
    // face (skin)
    ctx.fillStyle = '#a08068';
    ctx.fillRect(13, 8, 6, 6);
    // hood shadow over face top
    ctx.fillStyle = '#1a1a24';
    ctx.fillRect(13, 7, 6, 2);
    // eyes
    ctx.fillStyle = '#000';
    ctx.fillRect(14, 10, 1, 1);
    ctx.fillRect(17, 10, 1, 1);
    // arms (one at side, slight tilt)
    ctx.fillStyle = '#2a2a3a';
    ctx.fillRect(7, 18, 3, 8);
    ctx.fillRect(22, 18, 3, 8);
    // belt
    ctx.fillStyle = '#3a2a14';
    ctx.fillRect(10, 24, 12, 1);
    // outline
    ctx.fillStyle = '#000';
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.strokeRect(10, 16, 12, 14);
  }, 'tex.npc', scene);
}

/** 16×16 pixel sprite for a chest (loot drop). */
export function chestSpriteTexture(scene: Scene): Texture {
  return px(16, (ctx) => {
    ctx.clearRect(0, 0, 16, 16);
    // chest body
    ctx.fillStyle = '#5a3e22';
    ctx.fillRect(2, 6, 12, 8);
    // chest lid (rounded)
    ctx.fillStyle = '#7a5a32';
    ctx.fillRect(2, 4, 12, 4);
    // metal bands
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(2, 7, 12, 1);
    ctx.fillRect(2, 12, 12, 1);
    ctx.fillRect(7, 4, 1, 10);
    // lock
    ctx.fillStyle = '#c0a020';
    ctx.fillRect(7, 9, 2, 2);
    // outline
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.strokeRect(2, 4, 12, 10);
  }, 'tex.chest', scene);
}

/** 32×32 pixel sprite for an enemy — a goblin-like figure. */
export function enemySpriteTexture(scene: Scene): Texture {
  return px(32, (ctx) => {
    ctx.clearRect(0, 0, 32, 32);
    // body (green)
    ctx.fillStyle = '#3a5a2a';
    ctx.fillRect(10, 14, 12, 16);
    // shoulders
    ctx.fillRect(8, 12, 16, 4);
    // head
    ctx.fillStyle = '#4a6a3a';
    ctx.fillRect(11, 4, 10, 10);
    // ears (pointy)
    ctx.fillStyle = '#3a5a2a';
    ctx.fillRect(8, 5, 3, 4);
    ctx.fillRect(21, 5, 3, 4);
    // eyes (red)
    ctx.fillStyle = '#c02020';
    ctx.fillRect(13, 7, 2, 2);
    ctx.fillRect(17, 7, 2, 2);
    // mouth
    ctx.fillStyle = '#000';
    ctx.fillRect(14, 11, 4, 1);
    // arms
    ctx.fillStyle = '#3a5a2a';
    ctx.fillRect(6, 16, 4, 10);
    ctx.fillRect(22, 16, 4, 10);
    // weapon (club)
    ctx.fillStyle = '#5a3e22';
    ctx.fillRect(3, 8, 2, 12);
    // outline
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.strokeRect(10, 14, 12, 16);
  }, 'tex.enemy', scene);
}

// ===========================================================================
// House textures (Issue #16)
// ===========================================================================

/** 64×64 stone foundation — irregular blocks. */
export function stoneFoundationTexture(scene: Scene): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#7a7060';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(20);
    for (let row = 0; row < 4; row++) {
      const offset = row % 2 === 0 ? 0 : 12;
      for (let col = -1; col < 5; col++) {
        const x = col * 24 + offset;
        const y = row * 16;
        ctx.fillStyle = rnd() < 0.5 ? '#8a8070' : '#6a6050';
        ctx.fillRect(x + 1, y + 1, 22, 14);
        ctx.fillStyle = '#2a241c';
        ctx.fillRect(x, y, 24, 1);
        ctx.fillRect(x, y + 15, 24, 1);
      }
    }
    for (let i = 0; i < 120; i++) {
      ctx.fillStyle = `rgba(0,0,0,${rnd() * 0.2})`;
      ctx.fillRect(Math.floor(rnd() * 64), Math.floor(rnd() * 64), 1, 1);
    }
  }, 'tex.stoneFoundation', scene);
}

/** 64×64 plaster wall — beige/cream with subtle cracks. */
export function plasterWallTexture(scene: Scene): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#c8b894';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(21);
    for (let i = 0; i < 300; i++) {
      ctx.fillStyle = `rgba(0,0,0,${rnd() * 0.18})`;
      ctx.fillRect(Math.floor(rnd() * 64), Math.floor(rnd() * 64), 1, 1);
    }
    // hairline cracks
    ctx.strokeStyle = 'rgba(60,40,20,0.4)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 5; i++) {
      ctx.beginPath();
      ctx.moveTo(rnd() * 64, rnd() * 64);
      ctx.lineTo(rnd() * 64, rnd() * 64);
      ctx.stroke();
    }
    // warmer highlight patches
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = `rgba(255,220,180,${rnd() * 0.1})`;
      ctx.fillRect(Math.floor(rnd() * 64), Math.floor(rnd() * 64), 2, 2);
    }
  }, 'tex.plasterWall', scene);
}

/** 64×64 timber-frame beam — dark wood for the visible Fachwerk. */
export function timberBeamTexture(scene: Scene): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#3a2a18';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(22);
    for (let i = 0; i < 6; i++) {
      const y = i * 11;
      ctx.fillStyle = rnd() < 0.5 ? '#4a3a22' : '#2a1a08';
      ctx.fillRect(0, y, 64, 9);
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.fillRect(0, y + 8, 64, 1);
      // grain
      for (let g = 0; g < 12; g++) {
        ctx.fillStyle = 'rgba(0,0,0,0.3)';
        ctx.fillRect(Math.floor(rnd() * 64), y + Math.floor(rnd() * 8), 1, 1);
      }
    }
  }, 'tex.timberBeam', scene);
}

/** 32×64 wooden plank door with iron straps. */
export function doorWoodTexture(scene: Scene): Texture {
  return px(32, (ctx) => {
    ctx.fillStyle = '#5a3a1c';
    ctx.fillRect(0, 0, 32, 64);
    const rnd = mulberry32(23);
    // 3 vertical planks
    for (let i = 0; i < 3; i++) {
      const x = i * 11;
      ctx.fillStyle = rnd() < 0.5 ? '#6a4828' : '#4a2a14';
      ctx.fillRect(x, 0, 10, 64);
      ctx.fillStyle = '#1a0a04';
      ctx.fillRect(x + 9, 0, 1, 64);
      // grain
      for (let g = 0; g < 8; g++) {
        ctx.fillStyle = 'rgba(0,0,0,0.25)';
        ctx.fillRect(x + Math.floor(rnd() * 10), Math.floor(rnd() * 64), 1, 1);
      }
    }
    // iron bands (horizontal)
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 8, 32, 2);
    ctx.fillRect(0, 30, 32, 2);
    ctx.fillRect(0, 54, 32, 2);
    // iron studs
    ctx.fillStyle = '#2a2a2a';
    for (let y of [10, 12, 32, 34, 56, 58]) {
      for (let x of [3, 11, 19, 27]) {
        ctx.fillRect(x, y, 2, 2);
      }
    }
    // handle (right side, mid-height)
    ctx.fillStyle = '#3a3a3a';
    ctx.fillRect(22, 30, 2, 4);
    ctx.fillStyle = '#c0a020';
    ctx.fillRect(24, 31, 3, 2);
  }, 'tex.doorWood', scene);
}

/** 16×16 roof shingle — overlapping clay tiles. */
export function roofShingleTexture(scene: Scene): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#3a1410';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(24);
    // 4 rows of tiles (16px tall each)
    for (let row = 0; row < 4; row++) {
      const y = row * 16;
      const offset = row % 2 === 0 ? 0 : 16;
      for (let col = -1; col < 5; col++) {
        const x = col * 32 + offset;
        ctx.fillStyle = rnd() < 0.4 ? '#5a1c14' : rnd() < 0.5 ? '#6a2c1c' : '#4a140c';
        ctx.fillRect(x + 1, y, 30, 14);
        // bottom shadow line
        ctx.fillStyle = '#1a0408';
        ctx.fillRect(x + 1, y + 13, 30, 1);
        // top highlight
        ctx.fillStyle = 'rgba(255,200,150,0.15)';
        ctx.fillRect(x + 2, y + 1, 28, 1);
      }
    }
  }, 'tex.roofShingle', scene);
}

/** 64×64 interior wood floor — horizontal planks. */
export function woodFloorTexture(scene: Scene): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#6a4828';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(25);
    for (let i = 0; i < 8; i++) {
      const y = i * 8;
      ctx.fillStyle = rnd() < 0.5 ? '#7a5838' : '#5a3818';
      ctx.fillRect(0, y, 64, 7);
      ctx.fillStyle = '#2a1408';
      ctx.fillRect(0, y + 7, 64, 1);
      for (let g = 0; g < 4; g++) {
        ctx.fillStyle = 'rgba(0,0,0,0.25)';
        ctx.fillRect(Math.floor(rnd() * 64), y + Math.floor(rnd() * 7), 1, 1);
      }
    }
  }, 'tex.woodFloor', scene);
}

/** 32×32 ceiling beam — exposed roof timber from below. */
export function ceilingBeamTexture(scene: Scene): Texture {
  return px(32, (ctx) => {
    ctx.fillStyle = '#3a2818';
    ctx.fillRect(0, 0, 32, 32);
    const rnd = mulberry32(26);
    ctx.fillStyle = rnd() < 0.5 ? '#4a3220' : '#2a1808';
    ctx.fillRect(0, 0, 32, 30);
    ctx.fillStyle = 'rgba(0,0,0,0.4)';
    ctx.fillRect(0, 30, 32, 1);
    for (let g = 0; g < 8; g++) {
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.fillRect(Math.floor(rnd() * 32), Math.floor(rnd() * 30), 1, 1);
    }
  }, 'tex.ceilingBeam', scene);
}

/** 16×16 straw / thatch for bedding. */
export function strawTexture(scene: Scene): Texture {
  return px(16, (ctx) => {
    ctx.fillStyle = '#c8a040';
    ctx.fillRect(0, 0, 16, 16);
    const rnd = mulberry32(27);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = rnd() < 0.5 ? '#d8b050' : '#a88030';
      ctx.fillRect(Math.floor(rnd() * 16), Math.floor(rnd() * 16), 1, 1);
    }
  }, 'tex.straw', scene);
}
// ===========================================================================
// House textures (Issues #19–#21 — visual style pass)
// ===========================================================================

/** 32×32 leaded window glass — dark green with two diagonal highlight bands. */
export function glassTexture(scene: Scene): Texture {
  return px(32, (ctx) => {
    ctx.fillStyle = '#2a3a30';
    ctx.fillRect(0, 0, 32, 32);
    // diagonal highlights (sun catch)
    ctx.strokeStyle = 'rgba(180,210,180,0.35)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(2, 6);
    ctx.lineTo(14, 18);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(20, 14);
    ctx.lineTo(30, 24);
    ctx.stroke();
    // slight noise to break up the flat fill
    const rnd = mulberry32(30);
    for (let i = 0; i < 60; i++) {
      ctx.fillStyle = `rgba(0,0,0,${rnd() * 0.18})`;
      ctx.fillRect(Math.floor(rnd() * 32), Math.floor(rnd() * 32), 1, 1);
    }
  }, 'tex.glass', scene);
}

/** 16×16 iron band — dark with a lighter centre for a rolled-metal hint. */
export function ironBandTexture(scene: Scene): Texture {
  return px(16, (ctx) => {
    ctx.fillStyle = '#0e0e0e';
    ctx.fillRect(0, 0, 16, 16);
    ctx.fillStyle = '#3a3a3a';
    ctx.fillRect(0, 6, 16, 4);
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 5, 16, 1);
    ctx.fillRect(0, 10, 16, 1);
    const rnd = mulberry32(31);
    for (let i = 0; i < 12; i++) {
      ctx.fillStyle = `rgba(255,255,255,${rnd() * 0.1})`;
      ctx.fillRect(Math.floor(rnd() * 16), 6 + Math.floor(rnd() * 4), 1, 1);
    }
  }, 'tex.ironBand', scene);
}

/** 64×64 furniture wood — finer grain than the floor planks. */
export function furnitureWoodTexture(scene: Scene): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#5a3a1c';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(32);
    // long-grain streaks
    for (let i = 0; i < 80; i++) {
      const y = Math.floor(rnd() * 64);
      const len = 8 + Math.floor(rnd() * 16);
      const x = Math.floor(rnd() * (64 - len));
      ctx.fillStyle = rnd() < 0.5 ? '#6a4828' : '#4a2a14';
      ctx.fillRect(x, y, len, 1);
    }
    // knots
    for (let i = 0; i < 4; i++) {
      const kx = Math.floor(rnd() * 64);
      const ky = Math.floor(rnd() * 64);
      ctx.fillStyle = '#2a1808';
      ctx.beginPath();
      ctx.arc(kx, ky, 2 + rnd() * 1.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }, 'tex.furnitureWood', scene);
}

/** 64×64 patterned rug — burgundy with a simple diamond border. */
export function rugTexture(scene: Scene): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#5a2418';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(33);
    // base noise
    for (let i = 0; i < 200; i++) {
      ctx.fillStyle = `rgba(0,0,0,${rnd() * 0.2})`;
      ctx.fillRect(Math.floor(rnd() * 64), Math.floor(rnd() * 64), 1, 1);
    }
    // diamond border (8×8 cells)
    ctx.strokeStyle = '#c0a060';
    ctx.lineWidth = 1;
    for (let x = 0; x < 64; x += 8) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, 64);
      ctx.stroke();
    }
    for (let y = 0; y < 64; y += 8) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(64, y);
      ctx.stroke();
    }
    // centre diamonds
    for (let cx = 16; cx < 64; cx += 16) {
      for (let cy = 16; cy < 64; cy += 16) {
        ctx.fillStyle = '#3a1408';
        ctx.fillRect(cx - 2, cy - 2, 5, 5);
        ctx.fillStyle = '#c0a060';
        ctx.fillRect(cx, cy, 1, 1);
      }
    }
  }, 'tex.rug', scene);
}

// ===========================================================================
// Nature textures (Grass / Trees / Water / Flowers / Rocks / Reeds)
// ===========================================================================

/** 64×64 meadow grass — lighter green with wild-flower specks. */
export function meadowGrassTexture(scene: Scene): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#527038';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(40);
    // dense base speckle (lighter than the standard grass so it reads as a sunlit meadow)
    for (let i = 0; i < 800; i++) {
      const c = rnd();
      ctx.fillStyle = c < 0.4 ? '#628848' : c < 0.7 ? '#426028' : '#6a9858';
      ctx.fillRect(Math.floor(rnd() * 64), Math.floor(rnd() * 64), 1, 1);
    }
    // scattered flower pixels (yellow, white, red, blue) — small clusters of 2-3 pixels
    const flowerColors = ['#f0d040', '#ffffff', '#d04030', '#6090e0', '#e080a8'];
    for (let i = 0; i < 35; i++) {
      const color = flowerColors[Math.floor(rnd() * flowerColors.length)];
      ctx.fillStyle = color;
      const fx = Math.floor(rnd() * 64);
      const fy = Math.floor(rnd() * 64);
      ctx.fillRect(fx, fy, 1, 1);
      if (rnd() < 0.5) ctx.fillRect(fx + 1, fy, 1, 1);
      if (rnd() < 0.4) ctx.fillRect(fx, fy + 1, 1, 1);
    }
  }, 'tex.meadowGrass', scene);
}

/** 64×64 wild/dry grass — yellower, sparser.  Used at the forest edge. */
export function wildGrassTexture(scene: Scene): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#5a5a28';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(41);
    for (let i = 0; i < 500; i++) {
      const c = rnd();
      ctx.fillStyle = c < 0.4 ? '#6a6a32' : c < 0.7 ? '#4a4a1e' : '#7a7a3a';
      ctx.fillRect(Math.floor(rnd() * 64), Math.floor(rnd() * 64), 1, 1);
    }
  }, 'tex.wildGrass', scene);
}

/** 32×32 tree bark — vertical streaks, dark brown. */
export function treeBarkTexture(scene: Scene): Texture {
  return px(32, (ctx) => {
    ctx.fillStyle = '#3a2418';
    ctx.fillRect(0, 0, 32, 32);
    const rnd = mulberry32(42);
    for (let i = 0; i < 50; i++) {
      const x = Math.floor(rnd() * 32);
      ctx.fillStyle = rnd() < 0.5 ? '#4a3220' : '#2a1808';
      ctx.fillRect(x, 0, 1 + Math.floor(rnd() * 2), 32);
    }
    // horizontal cracks
    for (let i = 0; i < 6; i++) {
      const y = Math.floor(rnd() * 32);
      const x0 = Math.floor(rnd() * 32);
      const w = 2 + Math.floor(rnd() * 6);
      ctx.fillStyle = '#1a0c04';
      ctx.fillRect(x0, y, w, 1);
    }
  }, 'tex.treeBark', scene);
}

/** 32×32 birch bark — pale with horizontal dark bands. */
export function birchBarkTexture(scene: Scene): Texture {
  return px(32, (ctx) => {
    ctx.fillStyle = '#d8d8c8';
    ctx.fillRect(0, 0, 32, 32);
    const rnd = mulberry32(43);
    // subtle vertical streaks
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgba(0,0,0,${rnd() * 0.18})`;
      ctx.fillRect(Math.floor(rnd() * 32), Math.floor(rnd() * 32), 1, 8);
    }
    // horizontal dark bands (birch lenticels)
    ctx.fillStyle = '#1a1a14';
    ctx.fillRect(0, 6, 32, 1);
    ctx.fillRect(0, 18, 32, 1);
    ctx.fillRect(0, 26, 32, 1);
    // eye-shaped scars
    ctx.fillStyle = '#2a2a1c';
    for (let i = 0; i < 3; i++) {
      const y = 3 + i * 10;
      ctx.fillRect(4 + Math.floor(rnd() * 24), y, 4, 1);
    }
  }, 'tex.birchBark', scene);
}

/** 32×32 oak leaves — dense dark green canopy with subtle internal shading. */
export function oakLeavesTexture(scene: Scene): Texture {
  return px(32, (ctx) => {
    ctx.fillStyle = '#2e4a20';
    ctx.fillRect(0, 0, 32, 32);
    const rnd = mulberry32(44);
    for (let i = 0; i < 600; i++) {
      const c = rnd();
      ctx.fillStyle = c < 0.4 ? '#3e5a2a' : c < 0.7 ? '#1e3a14' : '#4e6a3a';
      ctx.fillRect(Math.floor(rnd() * 32), Math.floor(rnd() * 32), 1, 1);
    }
    // a few lighter sun-catch pixels
    for (let i = 0; i < 25; i++) {
      ctx.fillStyle = `rgba(160,200,100,${0.3 + rnd() * 0.3})`;
      ctx.fillRect(Math.floor(rnd() * 32), Math.floor(rnd() * 32), 1, 1);
    }
  }, 'tex.oakLeaves', scene);
}

/** 32×32 pine needles — very dark green with a hint of needle texture. */
export function pineLeavesTexture(scene: Scene): Texture {
  return px(32, (ctx) => {
    ctx.fillStyle = '#1a2a18';
    ctx.fillRect(0, 0, 32, 32);
    const rnd = mulberry32(45);
    for (let i = 0; i < 700; i++) {
      const c = rnd();
      ctx.fillStyle = c < 0.4 ? '#2a3a22' : c < 0.7 ? '#0e1a0c' : '#3a4a30';
      ctx.fillRect(Math.floor(rnd() * 32), Math.floor(rnd() * 32), 1, 1);
    }
  }, 'tex.pineLeaves', scene);
}

/** 32×32 birch leaves — light, slightly yellowish green, more open canopy. */
export function birchLeavesTexture(scene: Scene): Texture {
  return px(32, (ctx) => {
    ctx.fillStyle = '#6a8a3a';
    ctx.fillRect(0, 0, 32, 32);
    const rnd = mulberry32(46);
    for (let i = 0; i < 500; i++) {
      const c = rnd();
      ctx.fillStyle = c < 0.4 ? '#7a9a4a' : c < 0.7 ? '#5a7a2a' : '#8aaa5a';
      ctx.fillRect(Math.floor(rnd() * 32), Math.floor(rnd() * 32), 1, 1);
    }
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgba(220,240,160,${0.25 + rnd() * 0.25})`;
      ctx.fillRect(Math.floor(rnd() * 32), Math.floor(rnd() * 32), 1, 1);
    }
  }, 'tex.birchLeaves', scene);
}

/** 64×64 water — animated blue surface with wave speckles.
 *  Phase A: lighter surface ripples (call twice per cycle). */
export function waterTexture(scene: Scene, seed: number): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#1e3a5a';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(seed);
    // base depth gradient (lighter in the middle)
    for (let i = 0; i < 300; i++) {
      ctx.fillStyle = `rgba(60,100,160,${rnd() * 0.4})`;
      ctx.fillRect(Math.floor(rnd() * 64), Math.floor(rnd() * 64), 1, 1);
    }
    // ripple lines (sparse diagonals)
    for (let i = 0; i < 8; i++) {
      const y0 = Math.floor(rnd() * 64);
      ctx.fillStyle = `rgba(140,180,220,${0.25 + rnd() * 0.3})`;
      for (let x = 0; x < 64; x += 2) {
        const yo = Math.floor(Math.sin((x + y0) * 0.4) * 1.5);
        if ((x + Math.floor(rnd() * 3)) % 4 === 0) {
          ctx.fillRect(x, y0 + yo, 1, 1);
        }
      }
    }
    // scattered white highlights (sun on water)
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = `rgba(220,240,255,${rnd() * 0.5})`;
      ctx.fillRect(Math.floor(rnd() * 64), Math.floor(rnd() * 64), 1, 1);
    }
  }, `tex.water.${seed}`, scene);
}

/** 16×16 small wildflower sprite — random colored cluster, used for meadow instances. */
export function flowerSpriteTexture(scene: Scene): Texture {
  return px(16, (ctx) => {
    ctx.clearRect(0, 0, 16, 16);
    // stem (subtle, green)
    ctx.fillStyle = '#3a5a2a';
    ctx.fillRect(7, 10, 1, 6);
    ctx.fillRect(8, 11, 1, 5);
    // petal cluster (top) — alternating colors via small set
    const palettes = [
      // yellow daisy
      ['#f0d040', '#f0d040', '#f0d040', '#a08020'],
      // white daisy
      ['#ffffff', '#f0e8d0', '#ffffff', '#a09060'],
      // red poppy
      ['#d04030', '#a02818', '#d04030', '#601008'],
      // blue bell
      ['#6090e0', '#4060a0', '#80a0e8', '#284068'],
      // pink clover
      ['#e080a8', '#c06080', '#f0a0c0', '#803858'],
    ];
    const palette = palettes[Math.floor(Math.random() * palettes.length)];
    ctx.fillStyle = palette[0];
    ctx.fillRect(6, 4, 4, 4);
    ctx.fillStyle = palette[1];
    ctx.fillRect(7, 3, 2, 1);
    ctx.fillRect(7, 8, 2, 1);
    ctx.fillStyle = palette[2];
    ctx.fillRect(7, 5, 2, 2);
    ctx.fillStyle = palette[3];
    ctx.fillRect(7, 5, 2, 1);
  }, 'tex.flower', scene);
}

/** 16×16 grass-blade sprite — small cluster of vertical green strokes, used
 *  for grass instances along the path and forest floor. */
export function grassBladeTexture(scene: Scene): Texture {
  return px(16, (ctx) => {
    ctx.clearRect(0, 0, 16, 16);
    const rnd = mulberry32(47);
    for (let i = 0; i < 20; i++) {
      const x = Math.floor(rnd() * 16);
      const h = 2 + Math.floor(rnd() * 5);
      ctx.fillStyle = rnd() < 0.5 ? '#4a6a32' : '#3a5a2a';
      ctx.fillRect(x, 16 - h, 1, h);
    }
  }, 'tex.grassBlade', scene);
}

/** 32×32 rock — irregular grey blocks with cracks and moss hint. */
export function rockTexture(scene: Scene): Texture {
  return px(32, (ctx) => {
    ctx.fillStyle = '#6a6258';
    ctx.fillRect(0, 0, 32, 32);
    const rnd = mulberry32(48);
    for (let i = 0; i < 200; i++) {
      const c = rnd();
      ctx.fillStyle = c < 0.4 ? '#7a7268' : c < 0.7 ? '#5a5248' : '#8a8278';
      ctx.fillRect(Math.floor(rnd() * 32), Math.floor(rnd() * 32), 1, 1);
    }
    // cracks
    ctx.strokeStyle = 'rgba(30,30,28,0.5)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.moveTo(rnd() * 32, rnd() * 32);
      ctx.lineTo(rnd() * 32, rnd() * 32);
      ctx.stroke();
    }
    // moss hint
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = `rgba(60,90,40,${0.3 + rnd() * 0.3})`;
      ctx.fillRect(Math.floor(rnd() * 32), Math.floor(rnd() * 32), 2, 2);
    }
  }, 'tex.rock', scene);
}

/** 16×32 reed — tall grass cluster, used at water edges. */
export function reedTexture(scene: Scene): Texture {
  return px(16, (ctx) => {
    ctx.clearRect(0, 0, 16, 32);
    const rnd = mulberry32(49);
    for (let i = 0; i < 18; i++) {
      const x = Math.floor(rnd() * 16);
      const h = 14 + Math.floor(rnd() * 14);
      ctx.fillStyle = rnd() < 0.5 ? '#6a7838' : '#4a5828';
      ctx.fillRect(x, 32 - h, 1, h);
    }
    // a few seed heads
    for (let i = 0; i < 3; i++) {
      const x = Math.floor(rnd() * 16);
      const y = Math.floor(rnd() * 8);
      ctx.fillStyle = '#a89048';
      ctx.fillRect(x, y, 1, 3);
    }
  }, 'tex.reed', scene);
}

// ===========================================================================
// World-scale nature (Issue follow-up — massive lake + filling the world)
// ===========================================================================

/** 64×64 deep lake water — darker blue, subtle wave speckles. */
export function lakeWaterTexture(scene: Scene, seed: number): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#162e4a';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(seed);
    // base depth speckle
    for (let i = 0; i < 350; i++) {
      ctx.fillStyle = `rgba(40,80,140,${rnd() * 0.4})`;
      ctx.fillRect(Math.floor(rnd() * 64), Math.floor(rnd() * 64), 1, 1);
    }
    // gentle long ripple lines
    for (let i = 0; i < 12; i++) {
      const y0 = Math.floor(rnd() * 64);
      ctx.fillStyle = `rgba(120,160,210,${0.2 + rnd() * 0.25})`;
      for (let x = 0; x < 64; x += 2) {
        const yo = Math.floor(Math.sin((x + y0) * 0.3) * 1.2);
        if ((x + Math.floor(rnd() * 4)) % 5 === 0) {
          ctx.fillRect(x, y0 + yo, 1, 1);
        }
      }
    }
    // scattered sun-catch highlights
    for (let i = 0; i < 50; i++) {
      ctx.fillStyle = `rgba(220,240,255,${rnd() * 0.55})`;
      ctx.fillRect(Math.floor(rnd() * 64), Math.floor(rnd() * 64), 1, 1);
    }
  }, `tex.lakeWater.${seed}`, scene);
}

/** 64×64 sandy beach — warm yellow-tan, scattered darker grains. */
export function sandTexture(scene: Scene): Texture {
  return px(64, (ctx) => {
    ctx.fillStyle = '#a89860';
    ctx.fillRect(0, 0, 64, 64);
    const rnd = mulberry32(60);
    for (let i = 0; i < 700; i++) {
      const c = rnd();
      ctx.fillStyle = c < 0.4 ? '#b8a878' : c < 0.7 ? '#988848' : '#c8b890';
      ctx.fillRect(Math.floor(rnd() * 64), Math.floor(rnd() * 64), 1, 1);
    }
    // a few darker pebbles
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgba(60,50,30,${0.4 + rnd() * 0.3})`;
      ctx.fillRect(Math.floor(rnd() * 64), Math.floor(rnd() * 64), 1, 1);
    }
    // damp patch near water (slightly darker on average)
    ctx.fillStyle = 'rgba(80,70,40,0.12)';
    ctx.fillRect(0, 48, 64, 16);
  }, 'tex.sand', scene);
}

/** 16×16 lily pad — green disc with a pink/white flower bud on top. */
export function lilyPadTexture(scene: Scene): Texture {
  return px(16, (ctx) => {
    ctx.clearRect(0, 0, 16, 16);
    // pad body
    ctx.fillStyle = '#2a5a28';
    ctx.fillRect(2, 4, 12, 10);
    ctx.fillStyle = '#3a6a32';
    ctx.fillRect(3, 5, 10, 8);
    // notch in the pad (classic lily shape)
    ctx.fillStyle = '#162e4a';
    ctx.fillRect(0, 8, 2, 2);
    // pad veins
    ctx.fillStyle = '#1a4a1a';
    for (let i = 0; i < 4; i++) {
      ctx.fillRect(4 + i * 2, 6, 1, 6);
    }
    // flower bud (random pink/white/yellow)
    const palette = ['#f0c0c0', '#ffffff', '#f0d060'];
    const c = palette[Math.floor(Math.random() * palette.length)];
    ctx.fillStyle = c;
    ctx.fillRect(7, 2, 3, 3);
    ctx.fillStyle = '#e8a0a0';
    ctx.fillRect(8, 1, 1, 1);
  }, 'tex.lilyPad', scene);
}

/** 12×12 mushroom sprite — red cap with white spots (classic Amanita). */
export function mushroomTexture(scene: Scene): Texture {
  return px(12, (ctx) => {
    ctx.clearRect(0, 0, 12, 12);
    // stem
    ctx.fillStyle = '#e8d8a8';
    ctx.fillRect(5, 7, 2, 5);
    ctx.fillStyle = '#c8b888';
    ctx.fillRect(5, 11, 2, 1);
    // cap
    ctx.fillStyle = '#c83820';
    ctx.fillRect(3, 4, 6, 4);
    ctx.fillStyle = '#a02818';
    ctx.fillRect(3, 7, 6, 1);
    // white spots on cap
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(4, 5, 1, 1);
    ctx.fillRect(7, 5, 1, 1);
    ctx.fillRect(6, 6, 1, 1);
  }, 'tex.mushroom', scene);
}

/** 32×32 deer billboard — pixel-art side view of a standing stag. */
export function deerTexture(scene: Scene): Texture {
  return px(32, (ctx) => {
    ctx.clearRect(0, 0, 32, 32);
    // legs (4 thin)
    ctx.fillStyle = '#5a3e22';
    ctx.fillRect(8, 22, 2, 8);
    ctx.fillRect(11, 22, 2, 8);
    ctx.fillRect(17, 22, 2, 8);
    ctx.fillRect(20, 22, 2, 8);
    // body (brown)
    ctx.fillStyle = '#7a5232';
    ctx.fillRect(7, 14, 18, 9);
    ctx.fillStyle = '#6a4828';
    ctx.fillRect(7, 21, 18, 2);
    // neck
    ctx.fillStyle = '#7a5232';
    ctx.fillRect(20, 9, 4, 7);
    // head
    ctx.fillStyle = '#7a5232';
    ctx.fillRect(22, 6, 5, 5);
    ctx.fillStyle = '#5a3e22';
    ctx.fillRect(26, 8, 1, 2);
    // ear
    ctx.fillStyle = '#5a3e22';
    ctx.fillRect(23, 5, 2, 2);
    // eye
    ctx.fillStyle = '#000';
    ctx.fillRect(25, 7, 1, 1);
    // antlers (small)
    ctx.fillStyle = '#3a2a18';
    ctx.fillRect(24, 2, 1, 4);
    ctx.fillRect(23, 3, 1, 1);
    ctx.fillRect(25, 3, 1, 1);
    ctx.fillRect(27, 2, 1, 4);
    ctx.fillRect(26, 3, 1, 1);
    ctx.fillRect(28, 3, 1, 1);
    // tail
    ctx.fillStyle = '#5a3e22';
    ctx.fillRect(6, 15, 2, 2);
    // outline
    ctx.strokeStyle = '#1a0e04';
    ctx.lineWidth = 1;
    ctx.strokeRect(7, 14, 18, 9);
  }, 'tex.deer', scene);
}

/** 16×16 small bird in flight — pixel silhouette against the sky. */
export function birdTexture(scene: Scene): Texture {
  return px(16, (ctx) => {
    ctx.clearRect(0, 0, 16, 16);
    ctx.fillStyle = '#1a1a1a';
    // M-shaped silhouette
    ctx.fillRect(2, 6, 1, 1);
    ctx.fillRect(3, 5, 2, 2);
    ctx.fillRect(5, 6, 1, 1);
    ctx.fillRect(6, 7, 2, 1);
    ctx.fillRect(8, 6, 1, 1);
    ctx.fillRect(9, 5, 2, 2);
    ctx.fillRect(11, 6, 1, 1);
    ctx.fillRect(12, 7, 2, 1);
  }, 'tex.bird', scene);
}

/** 16×16 small fern frond — for forest undergrowth. */
export function fernTexture(scene: Scene): Texture {
  return px(16, (ctx) => {
    ctx.clearRect(0, 0, 16, 16);
    const rnd = mulberry32(70);
    // central stem
    ctx.fillStyle = '#3a5a2a';
    ctx.fillRect(7, 14, 1, 2);
    // leaflets
    ctx.fillStyle = '#4a6a32';
    for (let i = 0; i < 6; i++) {
      const y = 2 + i * 2;
      const len = 5 - Math.floor(i / 2);
      ctx.fillRect(7 - len, y, len, 1);
      ctx.fillRect(8, y, len, 1);
    }
    // hint of lighter green
    for (let i = 0; i < 10; i++) {
      ctx.fillStyle = `rgba(120,180,80,${rnd() * 0.4})`;
      ctx.fillRect(Math.floor(rnd() * 16), Math.floor(rnd() * 16), 1, 1);
    }
  }, 'tex.fern', scene);
}

/** 32×32 weathered standing stone (menhir) — vertical slab with cracks. */
export function menhirTexture(scene: Scene): Texture {
  return px(32, (ctx) => {
    ctx.fillStyle = '#5a5a58';
    ctx.fillRect(0, 0, 32, 32);
    const rnd = mulberry32(80);
    // speckle
    for (let i = 0; i < 300; i++) {
      const c = rnd();
      ctx.fillStyle = c < 0.4 ? '#6a6a68' : c < 0.7 ? '#4a4a48' : '#7a7a78';
      ctx.fillRect(Math.floor(rnd() * 32), Math.floor(rnd() * 32), 1, 1);
    }
    // vertical crack
    ctx.strokeStyle = 'rgba(20,20,18,0.6)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(8 + rnd() * 4, 0);
    ctx.lineTo(10 + rnd() * 4, 32);
    ctx.stroke();
    // moss patches at the base
    ctx.fillStyle = 'rgba(60,90,40,0.35)';
    ctx.fillRect(4, 26, 8, 3);
    ctx.fillRect(18, 27, 6, 2);
    // lichen spots
    ctx.fillStyle = 'rgba(180,160,80,0.3)';
    ctx.fillRect(12, 8, 4, 4);
    ctx.fillRect(20, 14, 3, 3);
  }, 'tex.menhir', scene);
}

/** 32×32 fallen log — bark texture (uses treeBark as base, rotated/recoloured). */
export function fallenLogTexture(scene: Scene): Texture {
  return px(32, (ctx) => {
    ctx.fillStyle = '#2a1a08';
    ctx.fillRect(0, 0, 32, 32);
    const rnd = mulberry32(90);
    // horizontal bark streaks
    for (let i = 0; i < 50; i++) {
      const y = Math.floor(rnd() * 32);
      ctx.fillStyle = rnd() < 0.5 ? '#3a2818' : '#1a0c04';
      ctx.fillRect(0, y, 32, 1);
    }
    // knots
    for (let i = 0; i < 3; i++) {
      const kx = Math.floor(rnd() * 32);
      const ky = Math.floor(rnd() * 32);
      ctx.fillStyle = '#1a0c04';
      ctx.beginPath();
      ctx.arc(kx, ky, 2 + rnd() * 1.5, 0, Math.PI * 2);
      ctx.fill();
    }
    // moss hint along the top
    ctx.fillStyle = 'rgba(60,90,40,0.3)';
    ctx.fillRect(0, 4, 32, 4);
  }, 'tex.fallenLog', scene);
}

/** 16×16 small candle — wax body with a yellow flame on top. */
export function candleTexture(scene: Scene): Texture {
  return px(16, (ctx) => {
    // wax body
    ctx.fillStyle = '#e8d8a8';
    ctx.fillRect(6, 9, 4, 6);
    ctx.fillStyle = '#c8b888';
    ctx.fillRect(6, 14, 4, 1);
    // flame (transparent edge so it pops when emissive)
    ctx.fillStyle = '#ffe080';
    ctx.fillRect(7, 4, 2, 5);
    ctx.fillStyle = '#ff8000';
    ctx.fillRect(7, 6, 2, 3);
  }, 'tex.candle', scene);
}
